import {
  Button,
  CloseButton,
  Grid,
  Heading,
  Modal,
  Spinner,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useState } from "react";
import { CycleTypeService } from "../../../services/CyclesService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { CycleContext } from "../CyclesAndGoals";

export const CycleTypeEditModal = ({ open, setOpen, cycleType }) => {
  const { setCycleTypes } = useContext(CycleContext);
  const showNotification = useNotification();
  const [name, setName] = useState(cycleType?.name || "");
  const [duration, setDuration] = useState(cycleType?.duration || "");
  const [submitting, setSubmitting] = useState(false);

  async function handleCreateType() {
    try {
      setSubmitting(true);
      const cycleTypeObj = { name: name, duration: duration };
      const newCycleType = await CycleTypeService.createCycleType(cycleTypeObj);
      newCycleType.label = newCycleType.name;
      setCycleTypes((prevTypes) => [newCycleType, ...prevTypes]);
      setOpen(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("cyclesAndGoals.typeSelect.label"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("cyclesAndGoals.typeSelect.label"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateType() {
    try {
      setSubmitting(true);
      const cycleTypeObj = { id: cycleType.id, name: name, duration: duration };
      const updatedCycleType =
        await CycleTypeService.updateCycleType(cycleTypeObj);
      updatedCycleType.label = updatedCycleType.name;
      setCycleTypes((prevTypes) =>
        prevTypes.map((type) =>
          type.id === cycleType.id ? updatedCycleType : type
        )
      );
      setOpen(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("cyclesAndGoals.typeSelect.label"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("cyclesAndGoals.typeSelect.label"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setName(cycleType?.name || "");
    setDuration(cycleType?.duration || "");
  }

  return (
    <Modal
      open={open}
      onExited={handleClose}
      size="large"
      label={t("cyclesAndGoals.cycleType.newCycleType")}
    >
      <Modal.Header>
        <Grid hAlign="space-between">
          <Grid.Row>
            <Grid.Col>
              <Heading level="h2" as="h1">
                {t("cyclesAndGoals.cycleType.newCycleType")}
              </Heading>
            </Grid.Col>
            <Grid.Col width="auto">
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={() => setOpen(false)}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Header>
      <Modal.Body>
        <Grid colSpacing="small" rowSpacing="small" vAlign="middle">
          <Grid.Row>
            <Grid.Col width={4}>
              <TextInput
                value={name}
                id="cycle-type-name"
                renderLabel={t("cyclesAndGoals.cycleType.cycleTypeName")}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid.Col>
            <Grid.Col width={4}>
              <TextInput
                value={duration}
                id="cycle-type-duration"
                renderLabel={t("cyclesAndGoals.cycleType.cycleTypeDuration")}
                onChange={(e) => setDuration(e.target.value)}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          type="submit"
          onClick={() =>
            cycleType?.id ? handleUpdateType() : handleCreateType()
          }
          disabled={submitting}
        >
          {submitting ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
